import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, Button, Input } from '../components/Common';

export default function ClientVendors() {
  const { apiFetch, user } = useAuth();
  const [vendors, setVendors] = useState([]);
  const [form, setForm] = useState('');
  const [editing, setEditing] = useState(null);

  const clientId = user?.clientId;

  const load = useCallback(async () => {
    if (!clientId) return;
    try {
      const res = await apiFetch(`/client-vendors/${clientId}?includeInactive=true`);
      if (res.ok) setVendors(await res.json());
    } catch {}
  }, [apiFetch, clientId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.trim() || !clientId) return;
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? `/client-vendors/${clientId}/${editing}` : `/client-vendors/${clientId}`;
    const res = await apiFetch(url, { method, body: JSON.stringify({ name: form.trim() }) });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    setForm(''); setEditing(null);
    load();
  };

  const toggleActive = async (vendor) => {
    const res = await apiFetch(`/client-vendors/${clientId}/${vendor.id}`, {
      method: 'PUT', body: JSON.stringify({ isActive: !vendor.is_active })
    });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    load();
  };

  const remove = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.name}"?`)) return;
    const res = await apiFetch(`/client-vendors/${clientId}/${vendor.id}`, { method: 'DELETE' });
    if (!res.ok) { const err = await res.json(); alert(err.error); return; }
    load();
  };

  if (!clientId) return <Card><p style={{ color: 'var(--text-secondary)' }}>No client associated with your account.</p></Card>;

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}>Vendors</h1>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Manage your testing vendor list. These vendors appear in the engagement form when creating third-party tests.</p>
      </div>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input value={form} onChange={e => setForm(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save(); }}
            placeholder={editing ? 'Edit vendor name...' : 'Add a new vendor...'}
            style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 13, background: 'var(--bg-input)', color: 'var(--text)', outline: 'none', fontFamily: 'inherit' }} />
          <Button onClick={save} disabled={!form.trim()} style={{ fontSize: 13, padding: '10px 18px' }}>
            {editing ? 'Update' : '+ Add Vendor'}
          </Button>
          {editing && <Button variant="secondary" onClick={() => { setEditing(null); setForm(''); }} style={{ fontSize: 13, padding: '10px 14px' }}>Cancel</Button>}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {vendors.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 13, padding: '16px 0', textAlign: 'center' }}>No vendors configured. Add your first vendor above.</p>
          ) : vendors.map(v => (
            <div key={v.id} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 8,
              background: 'var(--bg-input)', opacity: v.is_active ? 1 : 0.5, transition: 'opacity 0.15s'
            }}>
              {v.is_msp_default && (
                <span style={{ fontSize: 10, fontWeight: 600, color: '#4f8fff', background: 'rgba(79,143,255,0.1)', padding: '3px 8px', borderRadius: 6 }}>MSP</span>
              )}
              <span style={{ flex: 1, fontSize: 14, fontWeight: 500 }}>{v.name}</span>
              {!v.is_active && <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 6 }}>Inactive</span>}
              {!v.is_msp_default && (
                <div style={{ display: 'flex', gap: 6 }}>
                  <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => { setEditing(v.id); setForm(v.name); }}>Edit</Button>
                  <Button variant="secondary" style={{ padding: '4px 10px', fontSize: 12, color: v.is_active ? '#f59e42' : '#34d399' }}
                    onClick={() => toggleActive(v)}>{v.is_active ? 'Deactivate' : 'Activate'}</Button>
                  {!v.is_active && (
                    <Button variant="danger" style={{ padding: '4px 10px', fontSize: 12 }}
                      onClick={() => remove(v)}>Delete</Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
